"use client";

import React, { createContext, useContext, ReactNode, useReducer } from "react";
import { Page, PageTab } from "../types";
import { Widget } from "@/modules/widgets";
import { cloneDeep } from "es-toolkit/compat";

export interface PendingNewPage {
  title: string;
  publishedToAll: boolean;
}

export interface GeneratingPage {
  sessionId: string;
  prompt: string;
}

interface PageState {
  pages: Page[];
  currentPageWidgetsData: Record<number, Widget & { data: any }>;
  currentPage: Page | null;
  currentTab: PageTab | null;
  pendingNewPage: PendingNewPage | null;
  generatingPage: GeneratingPage | null;
  loading: boolean;
  error: string | null;
}

type PageAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_PAGES"; payload: Page[] }
  | { type: "SET_CURRENT_PAGE"; payload: Page | null }
  | { type: "UPDATE_PAGE"; payload: Partial<Page> }
  | { type: "DELETE_PAGE"; payload: number }
  | { type: "UPDATE_PAGE_WIDGET"; payload: { pageId: number; widget: Widget } }
  | { type: "DELETE_PAGE_WIDGET"; payload: { pageId: number; widgetId: number } }
  | { type: "SET_CURRENT_PAGE_WIDGETS_DATA"; payload: Widget & { data: any } }
  | { type: "SET_CURRENT_TAB"; payload: PageTab | null }
  | { type: "SET_PENDING_NEW_PAGE"; payload: PendingNewPage | null }
  | { type: "SET_GENERATING_PAGE"; payload: GeneratingPage | null }

const initialState: PageState = {
  pages: [],
  currentPageWidgetsData: {},
  currentPage: null,
  currentTab: null,
  pendingNewPage: null,
  generatingPage: null,
  loading: false,
  error: null,
};

const pageReducer = (state: PageState, action: PageAction): PageState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "SET_PAGES":
      return {
        ...state,
        pages: action.payload,
        loading: false
      };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPageWidgetsData: {}, currentPage: action.payload };
    case "UPDATE_PAGE":
      return {
        ...state,
        pages: state.pages.map((page) =>
          page.id === action.payload.id ? { ...page, ...action.payload } : page
        ),
        currentPage: (action.payload?.id === state.currentPage?.id ? { ...state.currentPage, ...action.payload } : state.currentPage) as Page,
      };
    case "DELETE_PAGE":
      return {
        ...state,
        pages: state.pages.filter((page) => page.id !== action.payload),
        currentPage: state.currentPage?.id === action.payload ? null : state.currentPage,
      };
    case "DELETE_PAGE_WIDGET":
      const deleteWidgetId = Number(action.payload.widgetId);
      return cloneDeep(
        {
          ...state,
          pages: state.pages.map((page) =>
            page.id === action.payload.pageId ? { ...page, widgets: page.widgets?.filter((widget) => Number(widget.id) !== deleteWidgetId) } : page
          ),
          currentPage: {
            ...state.currentPage,
            widgets: state.currentPage?.widgets?.filter((widget) => Number(widget.id) !== deleteWidgetId),
          } as Page,
        }
      );
    case "UPDATE_PAGE_WIDGET":
      const updateWidgetId = Number(action.payload.widget.id);
      return {
        ...state,
        pages: state.pages.map((page) =>
          page.id === action.payload.pageId ? { ...page, widgets: page.widgets?.map((widget) => Number(widget.id) === updateWidgetId ? action.payload.widget : widget) } : page
        ),
        currentPage: {
          ...state.currentPage,
          widgets: state.currentPage?.widgets?.map((widget) => Number(widget.id) === updateWidgetId ? action.payload.widget : widget),
        } as Page,
      };
    case "SET_CURRENT_PAGE_WIDGETS_DATA":
      return {
        ...state,
        currentPageWidgetsData: { ...state.currentPageWidgetsData, [action.payload.id]: action.payload },
      };
    case "SET_CURRENT_TAB":
      return { ...state, currentTab: action.payload };
    case "SET_PENDING_NEW_PAGE":
      return { ...state, pendingNewPage: action.payload };
    case "SET_GENERATING_PAGE":
      return { ...state, generatingPage: action.payload };
    default:
      return state;
  }
};

const PageContext = createContext<{
  state: PageState;
  dispatch: React.Dispatch<PageAction>;
} | null>(null);
PageContext.displayName = "PageContext";

interface PageProviderProps {
  children: ReactNode;
}


export const PageProvider: React.FC<PageProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(pageReducer, initialState);

  return (
    <PageContext.Provider value={{ state, dispatch }}>
      {children}
    </PageContext.Provider>
  );
};

// Custom hook
export const usePage = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error("usePage must be used within a PageProvider");
  }
  return context;
};
